(() => {
  const config = window.__04_SHARED_CART__ || {};
  const bridge = config.bridge;
  if (!bridge) return;

  const rawFetch = window.fetch.bind(window);
  let syncing = false;
  let pushTimer = null;

  const nativeKey = (cart) =>
    (cart?.items || [])
      .map((item) => [String(item.variant_id), Number(item.quantity) || 0])
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map((pair) => pair.join(':'))
      .join('|');

  const sharedKey = (cart) =>
    (cart?.lines || [])
      .map((line) => [
        String(line.merchandiseId || '').split('/').pop(),
        Number(line.quantity) || 0,
      ])
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map((pair) => pair.join(':'))
      .join('|');

  async function getNativeCart() {
    const response = await rawFetch('/cart.js', {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'},
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Native cart unavailable');
    return response.json();
  }

  async function getSharedCart() {
    const response = await rawFetch(bridge, {
      credentials: 'include',
      headers: {Accept: 'application/json'},
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Shared cart unavailable');
    const payload = await response.json();
    return payload?.cart || null;
  }

  async function pushNativeToShared() {
    if (syncing) return;
    syncing = true;
    try {
      const cart = await getNativeCart();
      const lines = (cart.items || []).map((item) => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: Number(item.quantity) || 1,
      }));

      const body = new URLSearchParams();
      body.set('mode', 'replace');
      body.set('lines', JSON.stringify(lines));
      body.set('country', String(window.Shopify?.country || 'IT').toUpperCase());
      body.set('language', String(window.Shopify?.locale || 'en').slice(0,2).toUpperCase());

      await rawFetch(bridge, {
        method: 'POST',
        credentials: 'include',
        headers: {Accept: 'application/json'},
        body,
        keepalive: true,
      });
    } catch (error) {
      if (window.Shopify?.designMode) console.warn('[04] shared cart push failed', error);
    } finally {
      syncing = false;
    }
  }

  async function pullSharedToNative(shared) {
    if (syncing) return;
    syncing = true;
    try {
      await rawFetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: '{}',
      });

      const items = (shared?.lines || [])
        .map((line) => ({
          id: Number(String(line.merchandiseId || '').split('/').pop()),
          quantity: Number(line.quantity) || 1,
        }))
        .filter((line) => Number.isFinite(line.id) && line.id > 0);

      if (items.length) {
        const add = await rawFetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items}),
        });
        if (!add.ok) throw new Error('Could not import shared cart');
      }

      sessionStorage.setItem('04-cart-synced', '1');
      window.location.reload();
    } catch (error) {
      if (window.Shopify?.designMode) console.warn('[04] shared cart pull failed', error);
      syncing = false;
    }
  }

  async function initialSync() {
    if (syncing) return;
    try {
      const [nativeCart, sharedCart] = await Promise.all([
        getNativeCart(),
        getSharedCart(),
      ]);

      if (sharedCart?.id) {
        if (nativeKey(nativeCart) !== sharedKey(sharedCart)) {
          await pullSharedToNative(sharedCart);
        }
      } else if ((nativeCart?.item_count || 0) > 0) {
        await pushNativeToShared();
      }
    } catch (error) {
      if (window.Shopify?.designMode) console.warn('[04] shared cart initial sync failed', error);
    }
  }

  function schedulePush() {
    clearTimeout(pushTimer);
    pushTimer = setTimeout(() => {
      pushNativeToShared();
    }, 180);
  }

  window.fetch = async function sharedCartAwareFetch(input, init) {
    const response = await rawFetch(input, init);
    try {
      const requestUrl =
        typeof input === 'string'
          ? new URL(input, window.location.href)
          : new URL(input.url, window.location.href);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      const nativeCartMutation =
        requestUrl.origin === window.location.origin &&
        method !== 'GET' &&
        /\/cart\/(add|change|update|clear)(\.js)?$/.test(requestUrl.pathname);

      if (nativeCartMutation) schedulePush();
    } catch (_) {}
    return response;
  };

  document.addEventListener('DOMContentLoaded', initialSync, {once: true});
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) initialSync();
  });
})();